import { FulfillmentProviderIds } from '../../../../enums/fulfillment-provider-ids';
import { ProviderConnectionTypes, ProviderInviteStatuses } from '../types';

export const isProviderActive = (provider) => {
  return provider.active;
};

export const isProviderNotActive = (provider) => {
  return !provider.active && provider.status !== ProviderInviteStatuses.pending;
};

export const isProviderActiveNotIntegrated = (provider) => {
  return provider.active && !provider.isIntegrated;
};

export const getActiveProviders = (providers) => {
  return providers.filter(isProviderActive);
};

export const getActiveNotIntegratedProviders = (providers) => {
  return providers.filter(isProviderActiveNotIntegrated);
};

export const extractExternalClientConnectionId = (provider) => {
  return provider.externalClientConnectionId;
};

export const extractClientExternalConnectionId = (provider) => {
  return provider.clientExternalConnectionId;
};

export const extractClientExternalConnectionIdFromProviders = (providers) => {
  return providers.map(extractClientExternalConnectionId);
};

export const extractActiveNotIntegratedExternalClientConnectionIdFromProviders = (providers) => {
  return getActiveNotIntegratedProviders(providers).map(extractClientExternalConnectionId);
};

export const createGenericFilteredMap = (data, extractKeyFn) => {
  const map = {};
  if (!data?.length) {
    return map;
  }
  data.forEach((dataUnit) => {
    const key = extractKeyFn(dataUnit);
    if (key) {
      map[key] = dataUnit;
    }
  });
  return map;
};

const createPackingSlipSharing = (dataGrid) => {
  const packingSlipSharing = dataGrid?.packingSlipSharing ?? {};
  return {
    isEnabled: !!packingSlipSharing.isEnabled,
    notSupported: !!packingSlipSharing.notSupported,
  };
};

export const getProvidersWithSharingData = (providers, dataGrid, getKeyFn) => {
  const dataGridMap = createGenericFilteredMap(dataGrid, getKeyFn);

  return providers.map((provider) => {
    const dataGridUnit = dataGridMap[getKeyFn(provider)];
    return {
      ...provider,
      carrierSharing: dataGridUnit?.carrierSharing,
      packingSlipSharing: createPackingSlipSharing(dataGridUnit),
    };
  });
};

const getFilteredGridData = (providers, dataGrid) => {
  const providersMap = createGenericFilteredMap(providers, extractExternalClientConnectionId);
  return dataGrid.filter((data) => providersMap[extractClientExternalConnectionId(data)]);
};

export const getSpecificProviders = (providersHandler, filterProviders) => {
  const providersByFilter = providersHandler.list.filter(filterProviders);

  const filteredDataGrid = getFilteredGridData(providersByFilter, providersHandler.dataGrid);

  const providersWithSharingData = getProvidersWithSharingData(providersByFilter, filteredDataGrid, extractClientExternalConnectionId);

  const activeProviders = getActiveProviders(providersWithSharingData);

  return {
    list: providersWithSharingData,
    dataGrid: filteredDataGrid,
    activeProviders,
    updateList: providersHandler.updateList,
    updateDataGridProvider: providersHandler.updateDataGridProvider,
    updateDataGridProviders: providersHandler.updateDataGridProviders,
  };
};

export const getVendors = (providersHandler) => {
  const filterProviders = (provider) => provider.connectionType === ProviderConnectionTypes.marketplaceToVendor;

  const getVendorInvitations = (invitations) =>
    invitations.filter((invitation) => invitation.inviteType === ProviderConnectionTypes.marketplaceToVendor);

  const data = getSpecificProviders(providersHandler, filterProviders);

  const pendingInvitations = getProvidersWithSharingData(
    getVendorInvitations(providersHandler.invitations),
    providersHandler.dataGrid,
    (invitation) => invitation.externalInviteId,
  );

  const pendingInvitationsWithCarrierSharing = pendingInvitations.map((invitation) => {
    return {
      ...invitation,
      carrierSharing: undefined,
    };
  });

  return {
    ...data,
    pendingInvitations: pendingInvitationsWithCarrierSharing,
  };
};

const supportedFillProviderIds = [FulfillmentProviderIds.AuctaneFulfillment];

export const isProviderIntegrated = (provider) => {
  return !supportedFillProviderIds.includes(provider.fillProviderId);
};

export const getFfProviders = (providersHandler) => {
  const filterProviders = (provider) => provider.connectionType !== ProviderConnectionTypes.marketplaceToVendor;

  const specificProviders = getSpecificProviders(providersHandler, filterProviders);

  const removeUnsupportedPropertiesForIntegratedProviders = (providers) => {
    return providers.map((provider) => {
      const isIntegrated = isProviderIntegrated(provider);
      return {
        ...provider,
        carrierSharing: isIntegrated ? { notSupported: true } : provider.carrierSharing,
        packingSlipSharing: isIntegrated ? { notSupported: true } : provider.packingSlipSharing,
        isIntegrated,
      };
    });
  };

  return {
    ...specificProviders,
    list: removeUnsupportedPropertiesForIntegratedProviders(specificProviders.list),
    activeProviders: removeUnsupportedPropertiesForIntegratedProviders(specificProviders.activeProviders),
  };
};
